/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography
} from '@mui/material';
import {
  Language as LanguageIcon,
  Check as CheckIcon
} from '@mui/icons-material';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

// Simplified language list for beta testing - only English, Spanish, and German
// This reduces complexity and API load while we perfect the translation system
const languages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
];

interface LanguageSwitcherProps {
  variant?: 'icon' | 'button' | 'compact';
  showCurrentLanguage?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'icon',
  showCurrentLanguage = false 
}) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      // Store language preference
      localStorage.setItem('i18nextLng', languageCode);
      
      // Update HTML lang attribute for accessibility
      document.documentElement.lang = languageCode;
      
      // Update dir attribute for RTL languages (none in our current supported languages)
      document.documentElement.dir = 'ltr';
      
    } catch (error) {
      logger.error('Failed to change language:', error);
    }
    handleClose();
  };

  if (variant === 'compact') {
    return (
      <>
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '1.2em', mr: 0.5 }}>
            {currentLanguage.flag}
          </Typography>
          {showCurrentLanguage && (
            <Typography variant="caption" sx={{ textTransform: 'uppercase' }}>
              {currentLanguage.code}
            </Typography>
          )}
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {languages.map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              selected={language.code === currentLanguage.code}
            >
              <ListItemIcon>
                <Typography sx={{ fontSize: '1.2em' }}>
                  {language.flag}
                </Typography>
              </ListItemIcon>
              <ListItemText
                primary={language.nativeName}
                secondary={language.name !== language.nativeName ? language.name : undefined}
              />
              {language.code === currentLanguage.code && (
                <CheckIcon color="primary" fontSize="small" />
              )}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-label="change language"
        aria-controls={open ? 'language-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        sx={{ color: 'inherit' }}
      >
        <LanguageIcon />
      </IconButton>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === currentLanguage.code}
          >
            <ListItemIcon>
              <Typography sx={{ fontSize: '1.2em' }}>
                {language.flag}
              </Typography>
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {language.nativeName}
                  </Typography>
                  {language.name !== language.nativeName && (
                    <Typography variant="caption" color="text.secondary">
                      {language.name}
                    </Typography>
                  )}
                </Box>
              }
            />
            {language.code === currentLanguage.code && (
              <CheckIcon color="primary" fontSize="small" />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher; 